<template>
  <v-main>
    <v-container fluid class="pa-0" style="height: calc(100vh - 64px)">
      <v-row no-gutters style="height: 100%">
        <!-- Left Sidebar: Chat List -->
        <v-col
          cols="12"
          md="4"
          lg="3"
          :class="{ 'd-none': selectedChat && $vuetify.breakpoint.smAndDown }"
          style="border-right: 1px solid #e0e0e0; height: 100%"
        >
          <ChatList
            @selectChat="handleChatSelection"
            @openNewChat="showNewChatDialog = true"
          />
        </v-col>

        <!-- Center: Message Thread -->
        <v-col
          v-if="selectedChat"
          cols="12"
          md="8"
          :lg="showGroupInfo && selectedChat.is_group ? 6 : 9"
          style="height: 100%"
        >
          <MessageThread
            :chat="selectedChat"
            @toggleGroupInfo="showGroupInfo = !showGroupInfo"
            @back="handleBack"
          />
        </v-col>

        <!-- Right: Group Info Panel (conditionally shown) -->
        <v-col
          v-if="showGroupInfo && selectedChat && selectedChat.is_group"
          cols="12"
          lg="3"
          class="d-none d-lg-block"
          style="border-left: 1px solid #e0e0e0; height: 100%"
        >
          <GroupInfoPanel
            :chat="selectedChat"
            @close="showGroupInfo = false"
          />
        </v-col>

        <!-- Empty state when no chat selected -->
        <v-col
          v-if="!selectedChat"
          cols="12"
          md="8"
          lg="9"
          class="d-none d-md-flex align-center justify-center flex-column"
          style="background: #e5ddd5"
        >
          <div
            class="text-center pa-8"
            style="background: rgba(255,255,255,0.7); border-radius: 16px; max-width: 360px"
          >
            <v-icon size="96" color="rgba(0,0,0,0.15)">mdi-message-outline</v-icon>
            <p class="text-h6 mt-4" style="color: #555; font-weight: 500">
              Keep your chats private
            </p>
            <p class="text-body-2 mt-1" style="color: #888">
              Select a conversation from the left to read messages
            </p>
          </div>
        </v-col>
      </v-row>
    </v-container>

    <!-- Dialogs -->
    <NewChatDialog
      v-model="showNewChatDialog"
      @chatCreated="handleChatCreated"
    />
    <ImagePreviewDialog
      v-model="showImagePreview"
      :images="previewImages"
      :initial-index="previewIndex"
    />

    <!-- Connection Status Snackbar -->
    <v-snackbar
      v-model="showConnectionStatus"
      :color="connected ? 'success' : 'error'"
      top
      :timeout="-1"
    >
      <v-icon left>{{ connected ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
      {{ connected ? 'Connected' : 'Connection lost. Reconnecting...' }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="showConnectionStatus = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-main>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import ChatList from './components/ChatList.vue';
import MessageThread from './components/MessageThread.vue';
import GroupInfoPanel from './components/GroupInfoPanel.vue';
import NewChatDialog from './components/NewChatDialog.vue';
import ImagePreviewDialog from './components/ImagePreviewDialog.vue';

export default {
  name: 'MessengerPage',

  components: {
    ChatList,
    MessageThread,
    GroupInfoPanel,
    NewChatDialog,
    ImagePreviewDialog
  },

  data() {
    return {
      showGroupInfo: false,
      showNewChatDialog: false,
      showImagePreview: false,
      previewImages: [],
      previewIndex: 0,
      showConnectionStatus: false,
      subscribedChannels: [],
      hasEverConnected: false
    };
  },

  computed: {
    ...mapState('chat', ['connected', 'chats']),
    ...mapGetters('chat', ['selectedChat'])
  },

  watch: {
    connected(newVal, oldVal) {
      if (newVal !== oldVal) {
        if (newVal) {
          // Only show "Connection restored" if it was previously lost (not initial connect)
          if (this.hasEverConnected) {
            this.showConnectionStatus = true;
            this.$toast.success('Connection restored');
            setTimeout(() => {
              this.showConnectionStatus = false;
            }, 3000);
          }
          this.hasEverConnected = true;
        } else {
          // Connection lost — always notify
          this.showConnectionStatus = true;
          this.$toast.warning('Connection lost. Reconnecting...');
        }
      }
    }
  },

  mounted() {
    // Fetch chats first
    this.fetchChats().then(() => {
      // Then connect WebSocket
      this.connectWebSocket();
    });

    // Listen for online/offline events
    window.addEventListener('online', this.handleOnline);
    window.addEventListener('offline', this.handleOffline);
  },

  beforeDestroy() {
    // Disconnect WebSocket
    this.disconnectWebSocket();

    // Remove event listeners
    window.removeEventListener('online', this.handleOnline);
    window.removeEventListener('offline', this.handleOffline);
  },

  methods: {
    ...mapActions('chat', [
      'fetchChats',
      'handleNewMessage',
      'handleTypingStart',
      'handleTypingStop',
      'handleUserOnline',
      'handleUserOffline'
    ]),

    handleChatSelection(chat) {
      this.$store.commit('chat/SET_SELECTED_CHAT', chat.id);

      // Fetch messages for this chat if not already loaded
      const messages = this.$store.state.chat.messages[chat.id];
      if (!messages || messages.length === 0) {
        this.$store.dispatch('chat/fetchMessages', { chatId: chat.id });
      }

      // On mobile, hide chat list when chat is selected
      if (this.$vuetify.breakpoint.smAndDown) {
        this.showGroupInfo = false;
      }
    },

    handleBack() {
      // On mobile, go back to chat list
      this.$store.commit('chat/SET_SELECTED_CHAT', null);
    },

    handleChatCreated(chat) {
      // Subscribe to the new chat's channel
      this.subscribeToChat(chat);

      // Select the chat
      this.$store.commit('chat/SET_SELECTED_CHAT', chat.id);

      this.showNewChatDialog = false;
    },

    openImagePreview(images, index = 0) {
      this.previewImages = Array.isArray(images) ? images : [images];
      this.previewIndex = index;
      this.showImagePreview = true;
    },

    connectWebSocket() {
      const userId = this.user.id;

      // Subscribe to user's private channel for personal notifications
      this.$echo
        .private(`user.${userId}`)
        .listen('.MessageSent', (data) => {
          console.log('[Messenger] New message received on user channel:', data);
          this.handleNewMessage(data);
        });

      console.log(`[Messenger] Subscribed to private-user.${userId}`);

      // Subscribe to all user's chats
      this.chats.forEach((chat) => {
        this.subscribeToChat(chat);
      });

      // Mark as connected
      this.$store.commit('chat/SET_CONNECTED', true);
      console.log('[Messenger] WebSocket connected');
    },

    subscribeToChat(chat) {
      // Prevent duplicate subscriptions
      if (this.subscribedChannels.includes(chat.id)) {
        return;
      }

      if (chat.is_group) {
        // Presence channel for groups
        this.$echo
          .join(`room.${chat.id}`)
          .here((users) => {
            console.log(`[Messenger] Users in room.${chat.id}:`, users);
            this.$store.commit('chat/SET_ONLINE_USERS', users.map((u) => u.id));
          })
          .joining((user) => {
            console.log(`[Messenger] User joined room.${chat.id}:`, user.name);
            this.handleUserOnline(user.id);
          })
          .leaving((user) => {
            console.log(`[Messenger] User left room.${chat.id}:`, user.name);
            this.handleUserOffline(user.id);
          })
          .listen('.MessageSent', (data) => {
            console.log(`[Messenger] Message received on room.${chat.id}:`, data);
            this.handleNewMessage(data);
          })
          .listenForWhisper('typing', (e) => {
            this.handleTypingStart({ chatId: chat.id, userId: e.userId });
          })
          .listenForWhisper('stopped-typing', (e) => {
            this.handleTypingStop({ chatId: chat.id, userId: e.userId });
          });

        console.log(`[Messenger] Subscribed to presence-room.${chat.id}`);
      } else {
        // Private channel for 1-on-1 chats
        this.$echo
          .private(`chat.${chat.id}`)
          .listen('.MessageSent', (data) => {
            console.log(`[Messenger] Message received on chat.${chat.id}:`, data);
            this.handleNewMessage(data);
          })
          .listenForWhisper('typing', (e) => {
            this.handleTypingStart({ chatId: chat.id, userId: e.userId });
          })
          .listenForWhisper('stopped-typing', (e) => {
            this.handleTypingStop({ chatId: chat.id, userId: e.userId });
          });

        console.log(`[Messenger] Subscribed to private-chat.${chat.id}`);
      }

      this.subscribedChannels.push(chat.id);
    },

    disconnectWebSocket() {
      // Leave all channels
      this.$echo.leaveAllChannels();
      this.subscribedChannels = [];
      this.$store.commit('chat/SET_CONNECTED', false);
      console.log('[Messenger] WebSocket disconnected');
    },

    handleOnline() {
      console.log('[Messenger] Network online');
      this.$toast.success('Network connection restored');
      this.reconnectWebSocket();
    },

    handleOffline() {
      console.log('[Messenger] Network offline');
      this.$toast.warning('Network connection lost');
      this.$store.commit('chat/SET_CONNECTED', false);
    },

    reconnectWebSocket() {
      // Disconnect first
      this.disconnectWebSocket();

      // Wait a bit then reconnect
      setTimeout(() => {
        this.fetchChats().then(() => {
          this.connectWebSocket();
        });
      }, 1000);
    }
  }
};
</script>

<style scoped>
/* No additional styles needed - using Vuetify classes */
</style>
